<template>
  <div class="group-details">
    <div v-if="displayUsersList" class="users-list-container">
      <toolbar
        :has-search="!isSubGroup"
        :search-placeholder="!loading && !isSubGroup ? 'Search (type 3+ characters)' : null"
        @search="filterUsers"
      >
        <template slot="title">
          <group-name :group="group" />
          <span v-if="displayArchived"> Archived</span>
          <span> Users</span>
        </template>
        <pagination
          :text="paginationText"
          @first="firstPage"
          @previous="previousPage"
          @next="nextPage"
          @last="lastPage"
        />
        <template slot="search">
          <autocomplete
            v-if="isSubGroup && canEdit"
            class="new-globals"
            :value="state.autoCompleteValue"
            :results="autocompleteUserSearchResults"
            placeholder="Search by name, email"
            @search="searchUsers"
            @selectResult="result => addUserToGroup(result)"
          />
        </template>
      </toolbar>
      <md-progress-bar v-if="loading || inviting" md-mode="indeterminate" />
      <md-table v-if="users.length && !error" class="users-table" :value="users" md-card @md-selected="onSelect">
        <md-table-row
          slot="md-table-row"
          slot-scope="{ item: userRow }"
          md-selectable="multiple"
          :md-disabled="!canEdit || isKeyOwner(userRow)"
        >
          <md-table-cell md-label="User Name">{{ userRow.given_name }} {{ userRow.family_name }}</md-table-cell>
          <md-table-cell md-label="Email">{{ userRow.email }}</md-table-cell>
          <md-table-cell
            md-label="Role"
          >
            {{ userStatus(userRow) }} {{ userRow.deleted && ' - Removed' }}
          </md-table-cell>
          <md-table-cell v-if="isSubGroup" md-label="Membership">
            {{ 'Standard' }}
          </md-table-cell>
          <md-table-cell v-if="canEdit || isInstructor" md-label="Actions">
            <md-menu :md-offset-x="-150" :md-offset-y="-36">
              <md-button class="md-icon-button" md-menu-trigger>
                <md-icon>more_vert</md-icon>
              </md-button>

              <md-menu-content>
                <md-menu-item v-if="!userRow.pending" @click="editUser(userRow)">Edit User</md-menu-item>
                <md-menu-item
                  v-if="!isInstructor && !isKeyOwner(userRow)"
                  @click="selectUsersForRemoval([userRow]); openRemovalConfirm()"
                >
                  Remove User
                </md-menu-item>
              </md-menu-content>
            </md-menu>
          </md-table-cell>
          <md-table-cell v-if="displayArchived" md-label="View Student Data">
            <a :href="getGradingUrl(userRow)">
              Go to Grading
            </a>
          </md-table-cell>
        </md-table-row>
      </md-table>
      <md-empty-state
        v-if="!loading && !users.length && !error"
        md-icon="devices_other"
        md-label="Nothing to show"
        :md-description="group ? 'No users to display' : 'Use the search bar to search for a user'"
      />
      <md-empty-state
        v-if="!loading && error"
        class="md-accent"
        md-icon="error"
        md-label="An error occurred"
        :md-description="error.message || 'Unknown error'"
      />
      <md-toolbar class="md-primary bottom-toolbar new-globals">
        <md-button
          v-show="canEdit && !isInstructor"
          class="md-raised md-accent"
          :disabled="!state.selectedUsersForRemoval.length"
          @click="openRemovalConfirm()"
        >
          Remove Users
          <span v-if="group && !isSubGroup">From Organization</span>
        </md-button>
        <md-button
          v-show="canEdit && isSubGroup"
          class="md-raised md-accent"
          :disabled="!state.selectedUsersForRemoval.length"
          @click="openRemovalConfirm(group.metadata.parent)"
        >
          Remove Users from Org
        </md-button>
        <div class="button-and-tooltip">
          <md-button
            v-if="displayInvite && !displayArchived"
            :disabled="seatsExceeded"
            class="md-raised md-secondary"
            @click="openInviteConfirm"
          >
            Invite Single User
          </md-button>
          <md-tooltip
            v-if="seatsExceeded"
          >
            You have more users than you have seats for. Contact VES to increase seat count or remove current users to invite more.
          </md-tooltip>
        </div>
        <div class="button-and-tooltip">
          <md-button
            v-if="displayInvite && !displayArchived"
            :disabled="seatsExceeded"
            class="md-raised md-secondary"
            @click="openBulkInviteModal"
          >
            Bulk Invite
          </md-button>
          <md-tooltip
            v-if="seatsExceeded"
          >
            You have more users than you have seats for. Contact VES to increase seat count or remove current users to invite more.
          </md-tooltip>
        </div>
        <div />
      </md-toolbar>
    </div>

    <div v-if="!!group && (displayKeyOwner || displaySeats)" class="update-cards">
      <md-card v-if="displayKeyOwner" class="key-owner-card new-globals">
        <md-progress-bar
          v-if="updating && (isUpdating('metadata.ownerId') || isUpdating('name'))"
          md-mode="indeterminate"
        />
        <md-card-header>
          <div class="md-title">
            <span v-if="isSubGroup">Group Info</span>
            <span v-if="!isSubGroup">Organization Info</span>
          </div>
          <span class="md-caption">Select a text field to modify its content</span>
        </md-card-header>
        <md-card-content v-if="group">
          <span
            v-if="updateError && (isUpdating('metadata.ownerId') || isUpdating('name'))"
          >{{ updateError.message || updateError.error || updateError }}</span>
          <form>
            <md-field>
              <label>
                <span v-if="isSubGroup">Group Name</span>
                <span v-if="!isSubGroup">Organization Name</span>
              </label>
              <md-input
                placeholder="Group Name"
                :value="stripPrefix(group.name)"
                :disabled="!canEdit"
                @input="value => updateGroupName(value.trim())"
              />
            </md-field>
            <md-field v-if="!isSubGroup && isSuperAdmin">
              <label>Product Type</label>
              <md-select
                v-model="group.metadata.productType"
                @md-selected="updateProductType"
              >
                <md-option value="EMS">EMS</md-option>
                <md-option value="NURSING">Nursing</md-option>
                <md-option value="MILITARY">Military</md-option>
              </md-select>
            </md-field>
            <md-autocomplete
              placeholder="Email"
              :md-options="users.map(u => u.email)"
              :value="selectedGroupAdmin && selectedGroupAdmin.email"
              :disabled="!canEdit"
              @input="value => updateGroupAdmin(value)"
            >
              <label>{{ isSubGroup ? 'Group Admin Email' : 'Email' }}</label>
            </md-autocomplete>
            <md-field>
              <label>
                {{ isSubGroup ? 'Group Admin' : 'Key Owner' }} Name
              </label>
              <md-input
                :placeholder="`${isSubGroup ? 'Group Admin' : 'Key Owner'} Name`"
                :value="selectedGroupAdmin && `${selectedGroupAdmin.given_name || ''} ${selectedGroupAdmin.family_name || ''}`"
                :disabled="true"
              />
            </md-field>
            <div class="checkbox-container">
              <md-checkbox
                v-if="!isSubGroup && displayOrgFlags"
                :value="get(state.updates, 'metadata.dev', group.metadata.dev)"
                name="dev"
                @change="checked => updateIsDev(!get(state.updates, 'metadata.dev', group.metadata.dev))"
              >
                Internal VES Org
              </md-checkbox>
              <md-checkbox
                v-if="!isSubGroup && displayOrgFlags"
                :value="get(state.updates, 'metadata.testing', group.metadata.testing)"
                name="testing"
                @change="checked => updateIsTesting(!get(state.updates, 'metadata.testing', group.metadata.testing))"
              >
                Testing Org
              </md-checkbox>
              <md-checkbox
                v-if="!isSubGroup && displayOrgFlags"
                :value="get(state.updates, 'metadata.debug', group.metadata.debug)"
                name="debug"
                @change="checked => updateIsDebug(!get(state.updates, 'metadata.debug', group.metadata.debug))"
              >
                Debug Org
              </md-checkbox>
              <md-checkbox
                v-if="!isSubGroup && displayOrgFlags"
                :value="get(state.updates, 'metadata.ai', group.metadata.ai)"
                name="ai"
                @change="checked => updateIsAiDialogue(!get(state.updates, 'metadata.ai', group.metadata.ai))"
              >
                AI Dialogue
              </md-checkbox>
            </div>
          </form>
        </md-card-content>
        <md-card-actions>
          <md-button
            v-if="canEdit"
            :disabled="!state.updates"
            class="md-raised md-secondary"
            @click="confirmUpdates"
          >
            Save
          </md-button>
        </md-card-actions>
      </md-card>


      <md-card v-if="!!group && !isSubGroup && displaySeats" class="seats-card">
        <md-progress-bar v-if="updating && isUpdating('metadata.seats')" md-mode="indeterminate" />
        <md-card-header>
          <div class="md-title">Number of User Seats</div>
        </md-card-header>
        <md-card-content v-if="group">
          <span
            v-if="updateError && isUpdating('metadata.seats')"
          >{{ updateError.message || updateError.error || updateError }}</span>
          <form>
            <md-field
              class="seats-field"
              :class="{exceeded: group.metadata.memberCount > group.metadata.seats}"
            >
              <span class="user-count">{{ group.metadata.memberCount || 0 }}/</span>
              <md-input
                ref="seatsInput"
                type="number"
                :value="group.metadata.seats"
                :disabled="seatEditMode !== 'EDIT' || !state.editingSeats"
                @input="value => updateSeats(parseInt(value))"
              />
              <md-icon
                v-if="seatEditMode === 'EDIT'"
                @click.native="state.editingSeats ? save() : editSeats()"
              >
                {{ state.editingSeats ? 'check' : 'edit' }}
              </md-icon>
            </md-field>
          </form>
        </md-card-content>
        <md-card-actions v-if="seatEditMode === 'REQUEST'" class="new-globals">
          <a href="mailto:support@vrpatients.com?subject=Requesting Seats" target="_blank">
            <md-button class="md-raised md-secondary">REQUEST SEAT +/-</md-button>
          </a>
        </md-card-actions>
      </md-card>
    </div>

    <md-dialog :md-active.sync="state.inviteModalOpen" class="new-globals" :md-fullscreen="false">
      <md-progress-bar v-if="inviting" md-mode="indeterminate" />
      <md-dialog-title>
        Invite New user
        <br>
        <span class="md-caption">Select a text field to modify its content</span>
      </md-dialog-title>

      <md-dialog-content>
        <span v-if="inviteError" class="red-3 color">
          {{ inviteError.message || inviteError.error || inviteError }}
        </span>
        <md-field>
          <label>Email</label>
          <md-input v-model.trim="state.invitedEmail" />
        </md-field>
        <md-field>
          <md-select
            :value="state.invitedRole"
            @input="role => state.invitedRole = role"
          >
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('SUPER_ADMIN')"
              value="SUPER_ADMIN"
            >
              {{ roleLabels['SUPER_ADMIN'] }}
            </md-option>
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('ADMIN')"
              value="ADMIN"
            >
              {{ roleLabels['ADMIN'] }}
            </md-option>
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('USER')"
              value="USER"
            >
              Instructor
            </md-option>
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('STUDENT')"
              value="STUDENT"
            >
              Student
            </md-option>
          </md-select>
        </md-field>
        <md-autocomplete
          v-if="!group"
          placeholder="Organization"
          :md-options="state.groupSearchPromise"
          :value="state.invitedOrg && state.invitedOrg.name"
          @md-changed="term => searchGroups(term)"
          @md-selected="value => updateInvitedOrg(value)"
        >
          <label>Organization</label>
          <template slot="md-autocomplete-item" slot-scope="{ item }">{{ item.name }}</template>
        </md-autocomplete>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary" @click="closeInviteModal()">Cancel</md-button>
        <md-button class="md-primary" @click="invite()">Submit</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog :md-active.sync="state.bulkInviteModalOpen" class="new-globals" :md-fullscreen="false">
      <md-dialog-title class="dialog-title-with-close">
        {{ 'Bulk Invite' }}
        <md-button class="md-icon-button" @click="closeBulkInviteModal()">
          <md-icon>close</md-icon>
        </md-button>
      </md-dialog-title>
      <md-dialog-content>
        <bulk-invite :org="group" @submitted="bulkInviteSubmitted" />
      </md-dialog-content>
    </md-dialog>

    <md-dialog :md-active.sync="state.editUserModalOpen" class="new-globals" :md-fullscreen="false">
      <md-progress-bar v-if="savingUser" md-mode="indeterminate" />
      <md-dialog-title>
        Edit User
        <br>
        <span class="md-caption">Select a text field to modify its content</span>
      </md-dialog-title>

      <md-dialog-content v-if="state.editedUser">
        <span
          v-if="saveUserError"
        >{{ saveUserError.message || saveUserError.error || saveUserError }}</span>
        <md-field>
          <label>First Name</label>
          <md-input v-model.trim="state.editedUser.given_name" />
        </md-field>
        <md-field>
          <label>Last Name</label>
          <md-input v-model.trim="state.editedUser.family_name" />
        </md-field>
        <md-field>
          <label>Email</label>
          <md-input v-model.trim="state.editedUser.email" />
        </md-field>
        <md-field v-if="!isInstructor">
          <label>Role</label>
          <md-select
            :value="userRole(state.editedUser)"
            :disabled="!canSetRole(state.editedUser.app_metadata.role)"
            @input="role => state.editedUser.app_metadata.role = role"
          >
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('SUPER_ADMIN')"
              value="SUPER_ADMIN"
            >
              {{ roleLabels['SUPER_ADMIN'] }}
            </md-option>
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('ADMIN')"
              value="ADMIN"
            >
              {{ roleLabels['ADMIN'] }}
            </md-option>
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('USER')"
              value="USER"
            >
              Instructor
            </md-option>
            <md-option
              class="role-select-option"
              :disabled="!canSetRole('STUDENT')"
              value="STUDENT"
            >
              Student
            </md-option>
          </md-select>
        </md-field>
        <md-autocomplete
          v-if="canChangeGroup"
          placeholder="Organization"
          :md-options="state.groupSearchPromise"
          :value="group ? group.name : state.editedUser.org ? state.editedUser.org.name : ''"
          @md-changed="term => searchGroups(term)"
          @md-selected="value => updateUserGroup(value)"
        >
          <label>Organization</label>
          <template slot="md-autocomplete-item" slot-scope="{ item }">{{ item.name }}</template>
        </md-autocomplete>
        <md-button
          class="md-raised md-secondary"
          @click="sendPasswordReset(state.editedUser)"
        >
          Send Password Reset
        </md-button>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary" @click="closeEditUserModal()">Cancel</md-button>
        <md-button class="md-primary" @click="saveEditedUser()">Submit</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog
      :md-active.sync="state.removalConfirmOpen"
      :md-fullscreen="false"
    >
      <md-dialog-title>
        {{ group ? `Remove users from the ${isSubGroup && !isRemovingFromParent ? 'group' : 'organization'} ?` : 'Remove users permanently ?' }}
      </md-dialog-title>
      <md-dialog-content>
        {{ group ? `This will not delete the users accounts but will remove them from the ${isSubGroup && !isRemovingFromParent ? 'group' : 'organization'}.` : 'This will permanently remove the users. This action is irreversible.' }}
        <md-list v-if="isRemovingFromParent" class="remove-users-list">
          <md-list-item
            v-for="userToRemove in state.selectedUsersForRemoval"
            :key="userToRemove.user_id"
          >
            <span>{{ userLabel(userToRemove) }}</span>
          </md-list-item>
        </md-list>
        <md-progress-bar v-if="removing" md-mode="indeterminate" />
      </md-dialog-content>

      <md-dialog-actions>
        <md-button :disabled="removing" @click="state.removalConfirmOpen = false">Cancel</md-button>
        <md-button
          class="md-primary"
          :disabled="removing"
          @click="removeSelectedUsers()"
        >
          Remove
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog-confirm
      :md-active.sync="state.updateConfirmOpen"
      md-title="Change Group Admin"
      md-content="Changing the Group Admin will prevent you from further editing this group. Continue ?"
      md-confirm-text="Confirm"
      md-cancel-text="Cancel"
      @md-confirm="save()"
    />

    <md-dialog :md-active.sync="state.invitesCompleteModalOpen" :md-fullscreen="false">
      <md-dialog-title>Invitations Sent!</md-dialog-title>
      <md-dialog-content />
      <md-dialog-actions>
        <md-button class="green-5 color" @click="closeInvitesCompleteModal">OK</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import isNumber from 'lodash/isNumber';
import isNaN from 'lodash/isNaN';
import debounce from 'lodash/debounce';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';

import Toolbar from '~lib/common/components/Toolbar';
import Autocomplete from '~lib/common/components/Autocomplete';
import { userLabel } from '~lib/common/utils/labels';

import GroupName, { stripPrefix } from '../../common/GroupName';
import BulkInvite from '../BulkInvite';
import Pagination from '~lib/common/components/Pagination.vue';

export default {
  name: 'group-details',
  components: {
    Toolbar,
    Autocomplete,
    GroupName,
    BulkInvite,
    Pagination,
  },
  props: {
    displayUsersList: {
      type: Boolean,
      default: true
    },
    displayKeyOwner: {
      type: Boolean,
      default: true
    },
    displaySeats: {
      type: Boolean,
      default: true
    },
    displayInvite: {
      type: Boolean,
      default: true
    },
    displayOrgFlags: {
      type: Boolean,
      default: false
    },
    displayArchived: {
      type: Boolean,
      default: false,
    },
    canChangeGroup: {
      type: Boolean,
      default: false
    },
    seatEditMode: {
      type: String,
      validator: v => ['EDIT', 'REQUEST'].includes(v),
      default: 'EDIT'
    },
    group: {
      type: Object,
      default: () => null
    },
    user: {
      type: Object,
      default: () => null
    },
    users: {
      type: Array,
      default: () => []
    },
    userSearchResults: {
      type: Array,
      default: () => []
    },
    groupSearchResults: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    updating: {
      type: Boolean,
      default: false
    },
    inviting: {
      type: Boolean,
      default: false
    },
    removing: {
      type: Boolean,
      default: false
    },
    savingUser: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: null
    },
    updateError: {
      type: Object,
      default: null
    },
    inviteError: {
      type: Object,
      default: null
    },
    saveUserError: {
      type: Object,
      default: null
    },
    paginationText: {
      type: String,
      default: null
    },
    groupAdmin: {
      type: Object,
      default: null
    },
    keyOwner: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      state: {
        selectedUsersForRemoval: [],
        removalConfirmOpen: false,
        inviteModalOpen: false,
        bulkInviteModalOpen: false,
        editUserModalOpen: false,
        updateConfirmOpen: false,
        invitesCompleteModalOpen: false,
        editingSeats: false,
        updates: null,
        invitedEmail: '',
        invitedOrg: null,
        invitedRole: 'STUDENT',
        editedUser: null,
        pendingUserGroupEdit: null,
        pendingRemovalGroupId: null,
        groupSearchPromise: [],
        autoCompleteValue: null,
      },
      roleLabels: {
        ADMIN: 'Admin',
        SUPER_ADMIN: 'Super Administrator',
        USER: 'Instructor',
        STUDENT: 'Student'
      }
    };
  },
  computed: {
    // This also updates even before the user saves the updates
    selectedGroupAdmin() {
      if (!this.group) {
        return null;
      }
      if (get(this.state.updates, 'metadata.ownerId')) {
        return this.users.find(
          u => u.user_id === this.state.updates.metadata.ownerId
        );
      }
      return this.groupAdmin;
    },
    canEdit() {
      if (this.displayArchived) {
        return false;
      }
      const { user } = this;
      return (
        user.role === 'ADMIN' ||
        user.role === 'SUPER_ADMIN' ||
        this.isGroupAdmin(this.user) ||
        this.isKeyOwner(this.user)
      );
    },
    isSuperAdmin() {
      const { user } = this;
      return (user.role === 'SUPER_ADMIN');
    },
    isInstructor() {
      const { user } = this;
      return (user.role === 'USER');
    },
    usersByEmail() {
      return keyBy(this.users, 'email');
    },
    usersById() {
      return keyBy(this.users, 'user_id');
    },
    seatsExceeded() {
      if (!this.group) {
        return this.loading ? true : false;
      }
      if (!this.users) {
        return true;
      }
      return this.group.metadata.seats <= this.group.metadata.memberCount;
    },
    isSubGroup() {
      if (!this.group) {
        return false;
      }
      return !!this.group.metadata.parent;
    },
    isRemovingFromParent() {
      if (!this.group) {
        return false;
      }
      return this.state.pendingRemovalGroupId === this.group.metadata.parent;
    },
    autocompleteUserSearchResults() {
      return this.userSearchResults
        .filter(user => !this.usersById[user.user_id])
        .map(user => ({
          id: user.user_id,
          label:
            user.given_name || user.family_name
              ? `${user.given_name} ${user.family_name}`
              : user.email,
          user
        }));
    }
  },
  watch: {
    updating(newVal, oldVal) {
      if (oldVal && !newVal && !this.updateError) {
        this.state.updates = null;
        this.cancelEditSeats();
      }
    },
    inviting(newVal, oldVal) {
      if (oldVal && !newVal && !this.inviteError) {
        this.state.invitedEmail = '';
        this.closeInviteModal();
      }
    },
    savingUser(newVal, oldVal) {
      if (oldVal && !newVal && !this.saveUserError) {
        this.state.editedUser = null;
        this.closeEditUserModal();
      }
    },
    removing(newVal, oldVal) {
      if (oldVal && !newVal && !this.error) {
        this.state.selectedUsersForRemoval = [];
        this.closeRemovalConfirm();
      }
    },
    groupSearchResults(groupSearchResults) {
      if (this.groupSearchPromiseResolver) {
        this.groupSearchPromiseResolver(groupSearchResults);
      }
    }
  },
  methods: {
    filterUsers: debounce(function filterUsers(term) {
      if (term.length > 0 && term.length < 3) {
        return;
      }
      this.$emit('filterUsers', term);
    }, 500),
    searchUsers: debounce(function searchUsers(term) {
      this.$emit('searchUsers', term);
    }, 500),
    searchGroups: debounce(function searchGroups(term) {
      if (!term) {
        return;
      }
      this.$emit('searchGroups', term);
      this.state.groupSearchPromise = new Promise(resolve => {
        this.groupSearchPromiseResolver = resolve;
      });
    }, 500),
    onSelect(selectedUsers) {
      this.state.selectedUsersForRemoval = selectedUsers;
    },
    isGroupAdmin(user) {
      return this.groupAdmin && this.groupAdmin.user_id == user.user_id;
    },
    isKeyOwner(user) {
      return this.keyOwner && this.keyOwner.user_id == user.user_id;
    },
    userRole(user) {
      if (!this.group) {
        return get(user, 'app_metadata.role');
      }
      return get(user, `app_metadata.orgSpecificRoles.${this.group._id}`) || get(user, 'app_metadata.role');
    },
    userStatus(user) {
      if (user.declined) {
        return 'Declined';
      } else if (user.pending) {
        return 'Pending Invite';
      }
      if (!this.isSubGroup && this.isKeyOwner(user)) {
        return 'Key Owner'
      }
      return this.roleLabels[this.userRole(user)] || 'Instructor';
    },
    canSetRole(role = 'USER') {
      const roleHierarchy = {
        SUPER_ADMIN: 4,
        ADMIN: 3,
        USER: 2,
        STUDENT: 1
      };
      const userRole = this.user['https://vrpatients.com/role'];
      if (roleHierarchy[userRole] >= roleHierarchy[role]) {
        return true;
      }
      return false;
    },
    getGradingUrl(user) {
      return `${process.env.VUE_APP_GRADING_PATH}?userId=${user.user_id}`;
    },
    selectUsersForRemoval(users) {
      this.state.selectedUsersForRemoval = users;
    },
    openRemovalConfirm(groupId) {
      this.state.pendingRemovalGroupId = groupId;
      this.state.removalConfirmOpen = true;
      this.$analytics.event('Org Management', 'user_remove', undefined, this.state.selectedUsersForRemoval.length);
    },
    closeRemovalConfirm() {
      this.state.removalConfirmOpen = false;
    },
    openInviteConfirm() {
      this.state.inviteModalOpen = true;
    },
    openBulkInviteModal() {
      this.state.bulkInviteModalOpen = true;
    },
    openInvitesCompleteModal() {
      this.state.invitesCompleteModalOpen = true;
    },
    closeInviteModal() {
      this.state.inviteModalOpen = false;
    },
    closeBulkInviteModal() {
      this.state.bulkInviteModalOpen = false;
    },
    closeInvitesCompleteModal() {
      this.state.invitesCompleteModalOpen = false;
    },
    editUser(user) {
      this.state.editedUser = cloneDeep(user);
      this.state.editedUser.app_metadata = this.state.editedUser
        .app_metadata || { role: 'USER' };
      this.openEditUserModal();
    },
    openEditUserModal() {
      this.state.editUserModalOpen = true;
      this.$analytics.event('Org Management', 'user_edit');
    },
    closeEditUserModal() {
      this.state.editUserModalOpen = false;
    },
    removeSelectedUsers() {
      this.$emit('removeUsers', this.state.selectedUsersForRemoval, this.state.pendingRemovalGroupId);
    },
    updateUserGroup(newGroup) {
      this.state.pendingUserGroupEdit = newGroup;
    },
    updateGroupAdmin(email) {
      if (!email) {
        return;
      }
      const user = this.usersByEmail[email];
      if (!user) {
        return;
      }
      this.state.updates = merge(this.state.updates, {
        metadata: { ownerId: user.user_id }
      });
    },
    updateGroupName(name) {
      this.state.updates = merge(this.state.updates, { name });
    },
    updateProductType(productType) {
      this.state.updates = merge(this.state.updates, {
        metadata: { productType }
      });
    },
    updateSeats(seats) {
      if (!isNumber(seats) || isNaN(seats)) {
        return;
      }
      this.state.updates = merge(this.state.updates, { metadata: { seats } });
    },
    updateIsDev(isDev) {
      this.state.updates = merge(this.state.updates, {
        metadata: { dev: isDev }
      });
    },
    updateIsTesting(isTesting) {
      this.state.updates = merge(this.state.updates, {
        metadata: { testing: isTesting }
      });
    },
    updateIsDebug(isDebug) {
      this.state.updates = merge(this.state.updates, {
        metadata: { debug: isDebug }
      });
    },
    updateIsAiDialogue(isAi) {
      this.state.updates = merge(this.state.updates, {
        metadata: { ai: isAi }
      });
    },
    editSeats() {
      this.state.editingSeats = true;
      this.$nextTick(() => {
        if (this.$refs.seatsInput) {
          this.$refs.seatsInput.$el.focus();
        }
      });
    },
    cancelEditSeats() {
      this.state.editingSeats = false;
    },
    isUpdating(field) {
      return !!get(this.state.updates, field);
    },
    confirmUpdates() {
      const { updates } = this.state;
      if (get(updates, 'metadata.ownerId') && !this.isKeyOwner(this.user) && (!get(updates, 'metadata.ownerId') === this.user.user_id)) {
        this.state.updateConfirmOpen = true;
      } else {
        this.save();
      }
    },
    updateInvitedOrg(org) {
      this.state.invitedOrg = org;
    },
    save() {
      if (this.state.updates === null) {
        return;
      }
      this.$emit('update', this.state.updates);
    },
    invite() {
      const invitedEmail = this.state.invitedEmail.toLowerCase();
      if (!invitedEmail) {
        return;
      }
      const { invitedOrg, invitedRole } = this.state;
      this.$emit('invite', invitedEmail, invitedOrg || this.group, invitedRole);
      this.$analytics.event('Org Management', 'user_invite');
    },
    addUserToGroup(userSearchResult) {
      const user = userSearchResult.user;
      this.$emit('addUser', user);
      // Cycling autoCompleteValue will cause the search box to clear after adding a user
      this.state.autoCompleteValue = '';
      this.$nextTick(() => {
        this.state.autoCompleteValue = null;
      });
    },
    saveEditedUser() {
      const { editedUser, pendingUserGroupEdit } = this.state;
      const existingUser = this.usersById[editedUser.user_id];
      const emailHasChanged = existingUser.email !== editedUser.email;
      this.$emit(
        'saveUser',
        pick(
          editedUser,
          'user_id',
          'given_name',
          'family_name',
          emailHasChanged && 'email',
          'app_metadata'
        ),
        pendingUserGroupEdit,
        this.group || editedUser.org
      );
      this.$analytics.event('Org Management', 'user_edit_submit');
    },
    sendPasswordReset(user) {
      this.$emit('resetPassword', user.email);
      this.$analytics.event('Org Management', 'reset_password');
    },
    bulkInviteSubmitted(newUsers) {
      this.closeBulkInviteModal();
      this.$emit('bulkInviteSubmitted', newUsers, this.group);
      this.openInvitesCompleteModal();
      this.$analytics.event('Org Management', 'user_bulk_invite', this.group.name, newUsers.length);
    },
    firstPage() {
      this.$emit('firstPage');
    },
    previousPage() {
      this.$emit('previousPage');
    },
    nextPage() {
      this.$emit('nextPage');
    },
    lastPage() {
      this.$emit('lastPage');
    },
    stripPrefix,
    get,
    userLabel,
  }
};
</script>

<style scoped>
.group-details {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  height: 100%;
}

.users-list-container {
  flex: 1;
  height: 100%;
}

.md-table {
  margin: 0;
  /* Height of container minus top and bottom toolbar */
  height: calc(100% - 64px - 64px);
}

.remove-users-list {
  max-height: 300px;
  overflow: auto;
}

.bottom-toolbar {
  display: flex;
  justify-content: flex-end;
}

.seats-card {
  margin-top: 38px;
}

.seats-field,
.seats-field input {
  font-size: 32px !important;
  line-height: 32px !important;
  color: #898989 !important;
  -webkit-text-fill-color: #898989 !important;
  font-weight: bold;
  flex: 0;
}

.seats-field input {
  max-width: 55px;
}

.seats-field .md-button {
  height: 32px;
  width: 32px;
}

.seats-field .md-icon {
  margin: 0;
  cursor: pointer;
}

.seats-field.exceeded .user-count,
.seats-field.exceeded input {
  color: #cd6262 !important;
  -webkit-text-fill-color: #cd6262 !important;
}

.md-tooltip {
  font-size: 12px;
}

.dialog-title-with-close {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 8px 0 24px;
}

.checkbox-container {
  display: flex;
  flex-direction: column;
}

.button-and-tooltip {
  margin-right: 8px;
}
</style>

<style>
.group-details .md-table-cell-selection {
  min-width: 40px;
}

/* Checked state */
.group-details .md-checkbox.md-checked .md-checkbox-container {
  background-color: #448aff !important;
  border-color: #448aff !important;
}

.role-select-option[disabled] button {
  text-decoration: line-through !important;
}
</style>